/**
 * keyboardInset.js — PROD-1194. The arithmetic behind useKeyboardInset, kept free of `window`
 * so scripts/test-keyboard-inset.mjs can hold it in node.
 *
 * On iPhone Safari the on-screen keyboard does NOT shrink the layout viewport: it covers the
 * bottom of it, and Safari pans the visual viewport (visualViewport.offsetTop) to keep the
 * focused field in sight. A bar pinned at the layout viewport's bottom is therefore behind the
 * keyboard. The covered height is what lies below the visible part:
 *
 *   inset = innerHeight - (visualViewport.offsetTop + visualViewport.height)
 *
 * Rounded, and never negative. Below KB_MIN_PX it is not a keyboard (Safari's toolbars resize
 * the visual viewport by a few pixels while they animate) and counts as 0.
 *
 * The iOS shell (Capacitor) resizes the web view itself, so there the layout already follows the
 * keyboard and the hook does nothing (see hooks/useKeyboardInset.js).
 */
export const KB_MIN_PX = 80

export function keyboardInset({ innerHeight, vvHeight, vvOffsetTop = 0 }) {
  if (![innerHeight, vvHeight, vvOffsetTop].every((n) => typeof n === 'number' && Number.isFinite(n))) return 0
  const covered = Math.round(innerHeight - (vvOffsetTop + vvHeight))
  return covered >= KB_MIN_PX ? covered : 0
}

/**
 * How far the focused field must move up so the lifted bar does not cover it: the overlap of
 * the field's bottom (plus a small gap) with the bar's top, in px; 0 when the field is clear.
 */
export function fieldOverlap(fieldRect, barRect, gap = 8) {
  if (!fieldRect || !barRect) return 0
  const over = Math.ceil(fieldRect.bottom + gap - barRect.top)
  return over > 0 ? over : 0
}
